import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="reload-on-close"
// Reloads the page once the modal this is on — and any modal turbo-swapped in
// after it — is finally dismissed. Used by the "You're live!" publish
// celebration and the unpublish / restore-to-draft confirmations: the event's
// status just changed, but the page behind was rendered for the old status.
// Rather than turbo-patch every fragment, reload so the whole page reflects the
// new state.
//
// The intent is tracked at MODULE scope, not on the element, because the modal
// can be destroyed without ever being "closed": a turbo_stream that updates
// #generalModal (e.g. the celebration modal's "QR code" button) replaces the
// wrapper's innerHTML, so Bootstrap never fires hidden.bs.modal and this
// controller just disconnects. That silently dropped the reload and left a
// published event still showing its draft "Publish event" card. Now the pending
// reload survives the swap and fires when the modal CHAIN ends.
let pendingReload = false
let watching = false
let observer = null

// Is the modal chain still alive? `.modal.show` alone is NOT a sufficient test:
// Bootstrap's show() fades the backdrop FIRST and only then does _showElement
// set display:block + .show, so a modal that is opening looks identical to no
// modal at all for the length of that transition — checking during that gap
// reloaded the page out from under a QR modal the user had just opened.
//
// Inline display tells the two apart, because Bootstrap only ever writes
// display:none when a modal actually hides (_hideModal):
//   ""      → freshly inserted, mid-open  → chain alive
//   "block" → open                        → chain alive
//   "none"  → opened and then closed      → chain ended
const chainAlive = () => {
  if (document.querySelector(".modal.show")) return true

  const wrapper = document.getElementById("generalModal")
  if (!wrapper) return false
  return Array.from(wrapper.querySelectorAll(".modal"))
              .some((modal) => modal.style.display !== "none")
}

const maybeReload = () => {
  if (!pendingReload) return
  // Settle first: on hidden.bs.modal a replacement modal may not be in the DOM
  // yet, and on a wrapper mutation the incoming modal hasn't started opening.
  setTimeout(() => {
    if (!pendingReload || chainAlive()) return
    pendingReload = false
    window.location.reload()
  }, 200)
}

const cancelReload = () => { pendingReload = false }

export default class extends Controller {
  connect() {
    pendingReload = true
    this.watch()
  }

  // Listeners are document / wrapper level and installed once — they must
  // outlive this controller, which disconnects the moment the modal it sits on
  // is swapped away.
  watch() {
    if (watching) return
    watching = true

    // hidden.bs.modal bubbles, so one document listener covers every modal in
    // the chain, including ones rendered long after this controller is gone.
    document.addEventListener("hidden.bs.modal", maybeReload)

    // Safety net for closes that never fire hidden.bs.modal — closeAllModals()
    // and the modal controller's cleanup both just wipe #generalModal's
    // innerHTML. Watch the wrapper and re-check whenever its content changes.
    const wrapper = document.getElementById("generalModal")
    if (wrapper) {
      observer = new MutationObserver(maybeReload)
      observer.observe(wrapper, { childList: true, subtree: true })
    }

    // Leaving the page makes the reload pointless (and would hand the next page
    // an unexpected one), so drop the intent on navigation.
    document.addEventListener("turbo:before-visit", cancelReload)
  }
}
